/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMEndpointEditorWrapperComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { AzureRMAutoServicePrincipalEndpointEditorComponent, IAzurRMComponentProps, IAzurRMComponentState } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMAutoServicePrincipalEndpointEditorComponent";
import { AzureRMManualFederationEndpointEditorComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMManualFederationEndpointEditorComponent";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { GenericEndpointsDetailsComponents } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericEndpointsDetailsComponents";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ServiceEndpointRequest, ServiceEndpointRequestResult, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IAzureRMEndpointEditorWrapperComponentState extends IAzurRMComponentState {
    isVerifyInProgress: boolean;
    isVerificationSuccess: boolean;
}

export class AzureRMEndpointEditorWrapperComponent extends VssComponent<IAzurRMComponentProps, IAzureRMEndpointEditorWrapperComponentState> {
    constructor(props: IAzurRMComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._sanitizeAndInitializeEndpointType(props, context);
        this._source = ServiceEndpointsSource.getInstance(context.pageContext);

        this._allowCreatingEndpointsInDraftStateEnabled = isFeatureFlagEnabled(context.pageContext, "ServiceEndpoints.AllowCreatingEndpointsInDraftState");
    }

    public render(): React.ReactNode {
        if (this._isAutoOidcFederation() || this._isAutoServicePrincipal() || this._isPublishProfileAuthScheme()) {
            return (
                <AzureRMAutoServicePrincipalEndpointEditorComponent {...this.props} />
            );
        }
        else if (this._allowCreatingEndpointsInDraftStateEnabled && this._isManualOidcFederation()) {
            return <AzureRMManualFederationEndpointEditorComponent {...this.props} />;
        }
        else if (this._isManualServicePrincipal() || this._isMsi() || this._isManualOidcFederation()) {
            return (
                <div>
                    <GenericAuthorizationComponent
                        disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                        serviceEndpointType={this._serviceEndpointType}
                        draftEndpoint={this.props.draftEndpoint}
                        isUpdateForm={this.props.isUpdateForm}
                        updateDraftEndpointState={this.props.updateDraftEndpointState}
                        updateInvalidInputsCount={this.props.updateInvalidInputsCount}
                    />
                    {/* This verificationcomponent is being rendered in other endpoint components as well. Ensure to make similar changes at other places if this is updated */}
                    { this._getVerificationComponent() }
                    <GenericEndpointsDetailsComponents
                        disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                        serviceEndpoint={this.props.draftEndpoint}
                        isUpdateForm={this.props.isUpdateForm}
                        updateDraftEndpointState={this.props.updateDraftEndpointState}
                        updateInvalidInputsCount={this.props.updateInvalidInputsCount}
                        updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                        allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
                    />
                </div>
            );
        }
        else {
            return null;
        }
    }

    private _getVerificationComponent(): React.ReactNode {
        if (this.props.isVerifiableConnection) {
            const isVerificationDisabled: boolean = (this.state && this.state.isVerifyInProgress) || this.props.getInvalidInputCount() > 1 || (this.props.getInvalidInputCount() === 1 && !!this.props.draftEndpoint.name && this.props.draftEndpoint.name.trim().length > 0);
            return (
                <div className={"flex-row default-input-margin-top"}>
                    { EndpointsEditorPanelUtils.getVerifyButton(isVerificationDisabled, this._verifyEndpoint) }
                    { this.state && EndpointsEditorPanelUtils.getVerificationDetails(this.state.isVerifyInProgress, this.state.isVerificationSuccess, this.props.errorMessage) }
                </div>
            );
        } else {
            return null;
        }
    }

    private _verifyEndpoint = (): void => {
        this._updateVerificationStatus(true, undefined);
        this._verifyConnection().then((serviceEndpointRequestResult: ServiceEndpointRequestResult) => {
            if (serviceEndpointRequestResult.errorMessage) {
                this._updateVerificationStatus(false, false);
                this.props.updateError(true, serviceEndpointRequestResult.errorMessage, Resources.VerificationFailed, serviceEndpointRequestResult.activityId);
            } else {
                this._updateVerificationStatus(false, true);
                this.props.updateError(false, "");
            }
        }).catch((error) => {
            this._updateVerificationStatus(false, false);
            const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
            this.props.updateError(true, errorMessage, Resources.VerificationFailed);
        });
    }

    private _updateVerificationStatus = (isVerifyInProgress: boolean, isVerificationSuccess: boolean): void => {
        this.setState({
            isVerifyInProgress: isVerifyInProgress,
            isVerificationSuccess: isVerificationSuccess
        });
    }

    private _verifyConnection(): Promise<ServiceEndpointRequestResult> {
        const serviceEndpointRequest: ServiceEndpointRequest = EndpointsEditorPanelUtils.getServiceEndpointRequestForTest(this.props.draftEndpoint);
        return this._source.verifyServiceEndpoint(serviceEndpointRequest, this.props.draftEndpoint.id);
    }

    private _isAutoServicePrincipal = (): boolean => this._isAutomatic( EndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal);

    private _isAutoOidcFederation = (): boolean => this._isAutomatic(EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation);

    private _isAutomatic = (endpointScheme: string): boolean => {
        if (Utils_String.equals(this.props.draftEndpoint.authorization.scheme, endpointScheme, true)
            && this.props.draftEndpoint.data.creationMode
            && Utils_String.equals(this.props.draftEndpoint.data.creationMode, EndpointConstants.AzureEndpointConstants.SpnCreationModeAutomatic, true)) {
                return true;
        }
        return false;
    }

    private _isManualServicePrincipal = (): boolean => this._isManual(EndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal);

    private _isManualOidcFederation = (): boolean => this._isManual(EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation);

    private _isManual = (endpointScheme: string): boolean => {
        if (Utils_String.equals(this.props.draftEndpoint.authorization.scheme, endpointScheme, true)
            && ((this.props.draftEndpoint.data.creationMode && Utils_String.equals(this.props.draftEndpoint.data.creationMode, EndpointConstants.AzureEndpointConstants.SpnCreationModeManual, true)) || !this.props.draftEndpoint.data.creationMode)) {
                return true;
        }
        return false;
    }

    private _isMsi = (): boolean => {
        if (Utils_String.equals(this.props.draftEndpoint.authorization.scheme, EndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity, true)) {
            return true;
        }
        return false;
    }

    private _isPublishProfileAuthScheme = (): boolean => {
        if (Utils_String.equals(this.props.draftEndpoint.authorization.scheme, EndpointConstants.EndpointAuthorizationSchemes.PublishProfile, true)) {
            return true;
        }
        return false;
    }

    private _sanitizeAndInitializeEndpointType(props: IAzurRMComponentProps, context: IVssComponentContext): void {
        let isHosted = AzureRMEndpointEditorWrapperComponent._isHostedEnvironment(context.pageContext);
        this._serviceEndpointType = props.serviceEndpointType;
        if (!isHosted) {
            this._serviceEndpointType.inputDescriptors.forEach((inputDescriptor) => {
                if (Utils_String.equals(inputDescriptor.id, EndpointConstants.AzureEndpointConstants.ScopeLevel, true)) {
                    //removing the ML workspace scope for onPrem deployment
                    inputDescriptor.values.possibleValues = inputDescriptor.values.possibleValues.filter(possibleValue => possibleValue.value !== EndpointConstants.AzureEndpointConstants.WorkspaceString);
                }
            });
        }

        // Endpoint in older onprem versions did not have these fields and hence, adding the default values
        if (this._isManualServicePrincipal() && this.props.isUpdateForm) {
            let environment = this.props.draftEndpoint.data[this.Environment];
            if (!environment || environment === "") {
                this.props.draftEndpoint.data[this.Environment] = this.DEFAULT_SELECTED_ENVIRONMENT;
            }

            let scopeLevel = this.props.draftEndpoint.data[this.ScopeLevel];
            if (!scopeLevel || scopeLevel === "") {
                this.props.draftEndpoint.data[this.ScopeLevel] = this.DEFAULT_SELECTED_SCOPE;
            }

            let creationMode = this.props.draftEndpoint.data[this.CreationMode];
            if (!creationMode || creationMode === "") {
                this.props.draftEndpoint.data[this.CreationMode] = this.DEFAULT_CREATION_MODE;
            }

            let authenticationType = this.props.draftEndpoint.authorization.parameters[this.AuthenticationType];
            if (!authenticationType || authenticationType === "") {
                this.props.draftEndpoint.authorization.parameters[this.AuthenticationType] = this.DEFAULT_AUTHENTICATION_TYPE;
            }

            // Below two fields, subscriptionId and subscriptionName, are being updated in their casing due to inconsistency
            // in the old and the new UI. Fixing the casing and deleting the older field with incorrect casing.
            let subscriptionId = this.props.draftEndpoint.data[this.SubscriptionId];
            if (subscriptionId) {
                this.props.draftEndpoint.data[this.subscriptionId] = subscriptionId;
                delete this.props.draftEndpoint.data[this.SubscriptionId];
            }

            let subscriptionName = this.props.draftEndpoint.data[this.SubscriptionName];
            if (subscriptionName) {
                this.props.draftEndpoint.data[this.subscriptionName] = subscriptionName;
                delete this.props.draftEndpoint.data[this.SubscriptionName];
            }
        }
    }

    private static _isHostedEnvironment(pageContext: IVssPageContext): boolean {
        if (pageContext) {
            const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
            if (vssPageService) {
                return vssPageService.getData().isHosted;
            }
        }

        return false;
    }

    private _allowCreatingEndpointsInDraftStateEnabled: boolean;

    private _serviceEndpointType: ServiceEndpointType;
    private _source: ServiceEndpointsSource;
    private DEFAULT_SELECTED_ENVIRONMENT: string = "AzureCloud";
    private DEFAULT_SELECTED_SCOPE: string = "Subscription";
    private DEFAULT_CREATION_MODE: string = "Manual";
    private DEFAULT_AUTHENTICATION_TYPE: string = "spnKey";
    private Environment: string = "environment";
    private ScopeLevel: string = "scopeLevel";
    private CreationMode: string = "creationMode";
    private AuthenticationType: string = "authenticationType";
    private SubscriptionId: string = "SubscriptionId";
    private subscriptionId: string = "subscriptionId";
    private SubscriptionName: string = "SubscriptionName";
    private subscriptionName: string = "subscriptionName";
}

VssComponent.register("endpoint-editor-component-azurerm", AzureRMEndpointEditorWrapperComponent);